// Клиент для `/api/v1/agent_roles`.
//
// Зеркалит pydantic-схемы backend'а 1:1 — ответы-списки отдаются голым
// массивом, без обёрток `{roles}` / `{history}`:
//   GET    /agent_roles                       → AgentRoleResponse[]
//   GET    /agent_roles/{role}                → AgentRoleResponse
//   PUT    /agent_roles/{role}/prompt         (body { prompt_text }) → AgentRoleResponse + warnings
//   DELETE /agent_roles/{role}/prompt         → AgentRoleResponse
//   GET    /agent_roles/{role}/history?limit= → RoleOverrideHistoryEntryResponse[]
//   GET    /agent_roles/models                → SupportedModelsResponse
//   PUT    /agent_roles/{role}/model          (body { model }) → AgentRoleResponse
//   DELETE /agent_roles/{role}/model          → AgentRoleResponse
//
// История промпта и модели — ОДНА общая лента (`kind: "prompt" | "model"`
// в каждой записи); отдельного эндпоинта для модели нет, потребители
// фильтруют общий ответ по `kind` (см. `OverrideHistory`). `org_id`
// берётся из JWT.
//
// Не-ok ответ — `ApiError` (`status`, `code`, `data` из `detail`), текст
// для человека строит `refusalText.ts` по паре статус + `code`; коды —
// `docs/spec/modules/agent_governance.md` § «Отказы». Без понятного тела —
// «<context>: HTTP <status>».

import { apiErrorFromResponse } from "../../api/apiError";
import { authedFetch } from "../../auth/authedFetch";

// Источник override'а: ручная правка (manual) или применённое
// предложение самообучения (self_improvement).
export type OverrideSource = "manual" | "self_improvement";

// Действие в общей истории override'ов: override задан впервые
// (create), изменён повторной правкой (update) или снят (delete).
export type HistoryAction = "create" | "update" | "delete";

// Какого override'а касается запись истории — промпта или модели.
export type OverrideKind = "prompt" | "model";

// Вид расхождения «промпт ↔ эффективный набор инструментов роли»:
// промпт упоминает инструмент вне набора (mentions_unavailable_tool),
// либо инструмент из набора не упомянут в промпте (tool_not_mentioned).
export type PromptWarningKind =
  | "mentions_unavailable_tool"
  | "tool_not_mentioned";

// AgentRoleResponse из backend-схемы. default_model/effective_model —
// «"" = модель провайдера по умолчанию».
export interface Role {
  readonly role_name: string;
  readonly default_system_prompt: string;
  readonly effective_system_prompt: string;
  readonly has_prompt_override: boolean;
  readonly prompt_override_source: OverrideSource | null;
  readonly default_model: string;
  readonly effective_model: string;
  readonly has_model_override: boolean;
  readonly model_override_source: OverrideSource | null;
}

// Предупреждение в ответе PUT /agent_roles/{role}/prompt о расхождении
// «новый промпт ↔ effective-набор инструментов роли».
export interface PromptToolWarning {
  readonly kind: PromptWarningKind;
  readonly tool_name: string;
}

// Ответ PUT /agent_roles/{role}/prompt — обновлённая роль + предупреждения.
export interface SavePromptResponse extends Role {
  readonly warnings: readonly PromptToolWarning[];
}

// RoleOverrideHistoryEntryResponse. Одна общая лента истории промпта и
// модели — различаются по `kind`. `value` — текст промпта или имя
// модели на момент правки; у `delete` — снятое значение. `edited_by` —
// null означает правку системным процессом (self_improvement apply).
export interface HistoryEntry {
  readonly kind: OverrideKind;
  readonly action: HistoryAction;
  readonly value: string;
  readonly source: OverrideSource;
  readonly edited_by: string | null;
  readonly created_at: string;
}

// Верхняя граница лимита истории на backend'е. Обе секции (промпт/
// модель, см. `OverrideHistory`) фильтруют один и тот же общий ответ на
// клиенте, поэтому запрашиваем максимум за один запрос вместо двух.
export const HISTORY_FETCH_LIMIT = 100;

// Ответ GET /agent_roles/models — модели активного провайдера org'а.
export interface SupportedModelsResponse {
  readonly models: readonly string[];
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

function roleUrl(roleName: string): string {
  return `/api/v1/agent_roles/${encodeURIComponent(roleName)}`;
}

export async function fetchRoles(): Promise<readonly Role[]> {
  const response = await authedFetch("/api/v1/agent_roles", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "roles");
  }
  return (await response.json()) as readonly Role[];
}

export async function fetchRole(roleName: string): Promise<Role> {
  const response = await authedFetch(roleUrl(roleName), {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "role");
  }
  return (await response.json()) as Role;
}

export async function saveOverride(
  roleName: string,
  promptText: string,
): Promise<SavePromptResponse> {
  const response = await authedFetch(`${roleUrl(roleName)}/prompt`, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify({ prompt_text: promptText }),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "saveOverride");
  }
  return (await response.json()) as SavePromptResponse;
}

export async function resetOverride(roleName: string): Promise<Role> {
  const response = await authedFetch(`${roleUrl(roleName)}/prompt`, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "resetOverride");
  }
  return (await response.json()) as Role;
}

export async function fetchHistory(
  roleName: string,
): Promise<readonly HistoryEntry[]> {
  const response = await authedFetch(
    `${roleUrl(roleName)}/history?limit=${String(HISTORY_FETCH_LIMIT)}`,
    { headers: buildHeaders() },
  );
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "history");
  }
  return (await response.json()) as readonly HistoryEntry[];
}

export async function fetchSupportedModels(): Promise<SupportedModelsResponse> {
  const response = await authedFetch("/api/v1/agent_roles/models", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "supportedModels");
  }
  return (await response.json()) as SupportedModelsResponse;
}

export async function saveModelOverride(
  roleName: string,
  model: string,
): Promise<Role> {
  const response = await authedFetch(`${roleUrl(roleName)}/model`, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify({ model }),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "saveModelOverride");
  }
  return (await response.json()) as Role;
}

export async function resetModelOverride(roleName: string): Promise<Role> {
  const response = await authedFetch(`${roleUrl(roleName)}/model`, {
    method: "DELETE",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "resetModelOverride");
  }
  return (await response.json()) as Role;
}
